import React, { useState, useEffect, useCallback } from 'react';
import { useNavStep } from '../lib/NavHistory';
import {
  Receipt, DollarSign, CreditCard, Plus, Eye, Send, FileText,
  AlertTriangle, CheckCircle, Clock, RefreshCw, X, Search, Filter,
  Download, ChevronLeft,
} from 'lucide-react';
import PrintButton from '../components/PrintButton';
import PatientDropdown from '../components/PatientDropdown';
import AIReportPanel from '../components/AIReportPanel';
import { NEU } from '../neuStyles';
import { matchesNameQuery } from '../lib/nameSearch';

// ─── Status badge ──────────────────────────────────────────────────────────

const STATUS_COLORS = {
  draft: { bg: '#f1f5f9', color: '#475569', label: 'Draft' },
  sent: { bg: '#dbeafe', color: '#1d4ed8', label: 'Sent' },
  partial: { bg: '#fef3c7', color: '#b45309', label: 'Partial' },
  paid: { bg: '#dcfce7', color: '#15803d', label: 'Paid' },
  overdue: { bg: '#fef2f2', color: '#DC3545', label: 'Overdue' },
  cancelled: { bg: '#f1f5f9', color: '#6C757D', label: 'Cancelled' },
  collections: { bg: '#fef2f2', color: '#991b1b', label: 'Collections' },
  write_off: { bg: '#f5f3ff', color: '#6d28d9', label: 'Write-off' },
};

function StatusBadge({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.draft;
  return (
    <span style={{ background: s.bg, color: s.color, padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, boxShadow: NEU.shadow.pill }}>
      {s.label}
    </span>
  );
}

function currency(val) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

// ─── KPI Card ──────────────────────────────────────────────────────────────

function KPI({ icon: Icon, label, value, color = '#2E8B98', sub }) {
  return (
    <div style={{ background: '#F8F9FA', borderRadius: 12, padding: '16px 20px', border: 'none', boxShadow: NEU.shadow.sm, flex: '1 1 160px', minWidth: 150 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 11, color: '#6C757D', fontWeight: 500 }}>{label}</span>
        <div style={{ width: 28, height: 28, borderRadius: 7, background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={14} color={color} />
        </div>
      </div>
      <div style={{ fontSize: 24, fontWeight: 700, color: '#212529' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#ADB5BD', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

// ─── Create Invoice Modal ──────────────────────────────────────────────────

function CreateInvoiceModal({ patients, feeSchedule, onClose, onCreated }) {
  const [patientId, setPatientId] = useState('');
  const [dueDate, setDueDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 30);
    return d.toISOString().split('T')[0];
  });
  const [notes, setNotes] = useState('');
  const [lineItems, setLineItems] = useState([{
    description: '', cptCode: '', icd10Code: '', quantity: 1, unitPrice: 0,
    hcpcsCode: '', ndcCode: '', modifier: '', placeOfService: '', diagnosisPointer: '', revenueCode: '',
  }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  function addLine() {
    setLineItems(prev => [...prev, {
      description: '', cptCode: '', icd10Code: '', quantity: 1, unitPrice: 0,
      hcpcsCode: '', ndcCode: '', modifier: '', placeOfService: '', diagnosisPointer: '', revenueCode: '',
    }]);
  }
  function removeLine(i) {
    setLineItems(prev => prev.filter((_, idx) => idx !== i));
  }
  function updateLine(i, field, value) {
    setLineItems(prev => prev.map((li, idx) => idx === i ? { ...li, [field]: value } : li));
  }
  function applyFee(i, cptCode) {
    const fee = feeSchedule.find(f => f.cpt_code === cptCode);
    if (fee) {
      setLineItems(prev => prev.map((li, idx) => idx === i ? {
        ...li, cptCode, description: fee.description, unitPrice: parseFloat(fee.default_fee),
      } : li));
    }
  }

  const subtotal = lineItems.reduce((s, li) => s + (li.quantity * li.unitPrice), 0);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!patientId || lineItems.length === 0 || lineItems.some(li => !li.description || li.unitPrice <= 0)) {
      setError('Please select a patient and add at least one valid line item.');
      return;
    }
    setSaving(true); setError(null);
    try {
      const lineItemsWithAllFields = lineItems.map(li => ({
        description: li.description,
        cptCode: li.cptCode,
        icd10Code: li.icd10Code,
        quantity: li.quantity,
        unitPrice: li.unitPrice,
        hcpcsCode: li.hcpcsCode,
        ndcCode: li.ndcCode,
        modifier: li.modifier,
        placeOfService: li.placeOfService,
        diagnosisPointer: li.diagnosisPointer,
        revenueCode: li.revenueCode,
      }));
      const result = await window.zignagenixAPI.createBillingInvoice({
        patientId: parseInt(patientId), encounterId: null, lineItems: lineItemsWithAllFields, dueDate, notes,
      });
      if (result.error) throw new Error(result.message);
      onCreated(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#F8F9FA', borderRadius: 16, width: 720, maxHeight: '85vh', overflow: 'auto', padding: 32, boxShadow: NEU.shadow.lg }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0, color: '#212529' }}>Create Invoice</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ADB5BD' }}><X size={20} /></button>
        </div>

        {error && <div style={{ background: '#fef2f2', border: 'none', borderRadius: 8, padding: '8px 12px', marginBottom: 16, color: '#DC3545', fontSize: 13, boxShadow: NEU.shadow.xs }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 4 }}>Patient *</label>
              <PatientDropdown
                patients={patients}
                value={patientId}
                onChange={setPatientId}
                placeholder="Select patient..."
                idPrefix="billing-inv"
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 4 }}>Due Date</label>
              <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: 'none', fontSize: 13, boxShadow: NEU.shadow.insetSm, background: '#F8F9FA' }} />
            </div>
          </div>

          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#212529' }}>Line Items</h3>
          {lineItems.map((li, i) => (
            <div key={i} className="responsive-scroll" style={{ borderBottom: 'none', boxShadow: 'inset 0 -1px 0 rgba(186,199,216,0.45)', paddingBottom: 12, marginBottom: 12 }}>
              {/* Row 1: CPT, HCPCS, Description, Qty, Unit Price, Remove */}
              <div style={{ display: 'grid', gridTemplateColumns: '100px 100px 1fr 80px 100px 32px', gap: 8, marginBottom: 8, alignItems: 'start', minWidth: 540 }}>
                <select value={li.cptCode} onChange={e => applyFee(i, e.target.value)}
                  style={{ padding: '7px 6px', borderRadius: 6, border: 'none', fontSize: 12, boxShadow: NEU.shadow.insetSm, background: '#F8F9FA' }}>
                  <option value="">CPT Code...</option>
                  {feeSchedule.map(f => <option key={f.cpt_code} value={f.cpt_code}>{f.cpt_code}</option>)}
                </select>
                <input placeholder="HCPCS Code" value={li.hcpcsCode} onChange={e => updateLine(i, 'hcpcsCode', e.target.value)}
                  style={{ padding: '7px 10px', borderRadius: 6, border: 'none', fontSize: 12, boxShadow: NEU.shadow.insetSm, background: '#F8F9FA' }} />
                <input placeholder="Description" value={li.description} onChange={e => updateLine(i, 'description', e.target.value)}
                  style={{ padding: '7px 10px', borderRadius: 6, border: 'none', fontSize: 12, boxShadow: NEU.shadow.insetSm, background: '#F8F9FA' }} />
                <input type="number" min="1" value={li.quantity} onChange={e => updateLine(i, 'quantity', parseInt(e.target.value) || 1)}
                  style={{ padding: '7px 6px', borderRadius: 6, border: 'none', fontSize: 12, textAlign: 'center', boxShadow: NEU.shadow.insetSm, background: '#F8F9FA' }} />
                <input type="number" step="0.01" min="0" value={li.unitPrice} onChange={e => updateLine(i, 'unitPrice', parseFloat(e.target.value) || 0)}
                  style={{ padding: '7px 6px', borderRadius: 6, border: 'none', fontSize: 12, textAlign: 'right', boxShadow: NEU.shadow.insetSm, background: '#F8F9FA' }} />
                {lineItems.length > 1 && (
                  <button type="button" onClick={() => removeLine(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC3545', padding: 4 }}><X size={14} /></button>
                )}
              </div>
              {/* Row 2: ICD-10, NDC, Modifier, Place of Service, Dx Pointer, Revenue Code */}
              <div style={{ display: 'grid', gridTemplateColumns: '100px 100px 100px 140px 100px 100px', gap: 8, alignItems: 'start', minWidth: 640 }}>
                <input placeholder="ICD-10 Code" value={li.icd10Code} onChange={e => updateLine(i, 'icd10Code', e.target.value)}
                  style={{ padding: '7px 10px', borderRadius: 6, border: 'none', fontSize: 12, boxShadow: NEU.shadow.insetSm, background: '#F8F9FA' }} />
                <input placeholder="NDC Code" value={li.ndcCode} onChange={e => updateLine(i, 'ndcCode', e.target.value)}
                  style={{ padding: '7px 10px', borderRadius: 6, border: 'none', fontSize: 12, boxShadow: NEU.shadow.insetSm, background: '#F8F9FA' }} />
                <input placeholder="Modifier" value={li.modifier} onChange={e => updateLine(i, 'modifier', e.target.value)}
                  style={{ padding: '7px 10px', borderRadius: 6, border: 'none', fontSize: 12, boxShadow: NEU.shadow.insetSm, background: '#F8F9FA' }} />
                <select value={li.placeOfService} onChange={e => updateLine(i, 'placeOfService', e.target.value)}
                  style={{ padding: '7px 6px', borderRadius: 6, border: 'none', fontSize: 12, boxShadow: NEU.shadow.insetSm, background: '#F8F9FA' }}>
                  <option value="">Place of Service...</option>
                  <option value="02">02: Telehealth Other</option>
                  <option value="10">10: Telehealth Home</option>
                  <option value="11">11: Office</option>
                  <option value="12">12: Home</option>
                  <option value="21">21: Inpatient Hospital</option>
                  <option value="22">22: On Campus-Outpatient</option>
                  <option value="23">23: Emergency Room</option>
                  <option value="31">31: Skilled Nursing</option>
                  <option value="81">81: Independent Lab</option>
                </select>
                <input placeholder="Dx Pointer (A,B,C,D)" value={li.diagnosisPointer} onChange={e => updateLine(i, 'diagnosisPointer', e.target.value)}
                  style={{ padding: '7px 10px', borderRadius: 6, border: 'none', fontSize: 12, boxShadow: NEU.shadow.insetSm, background: '#F8F9FA' }} />
                <input placeholder="Revenue Code" value={li.revenueCode} onChange={e => updateLine(i, 'revenueCode', e.target.value)}
                  style={{ padding: '7px 10px', borderRadius: 6, border: 'none', fontSize: 12, boxShadow: NEU.shadow.insetSm, background: '#F8F9FA' }} />
              </div>
            </div>
          ))}
          <button type="button" onClick={addLine} style={{
            background: '#F8F9FA', border: '1px dashed #cbd5e1', borderRadius: 8, padding: '8px 16px',
            cursor: 'pointer', fontSize: 12, color: '#2E8B98', fontWeight: 500, marginBottom: 16, width: '100%', boxShadow: NEU.shadow.xs,
          }}>
            <Plus size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Add Line Item
          </button>

          <div style={{ background: '#e8f7fa', border: 'none', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 12, color: '#062e3b', lineHeight: 1.5, boxShadow: NEU.shadow.sm }}>
            <strong>Billing Standards:</strong> Claims comply with CMS-1500 / UB-04 standards. ICD-10-CM (diagnosis), CPT/HCPCS (procedures), NDC (drugs), Revenue Codes (institutional).
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#F8F9FA', borderRadius: 8, marginBottom: 16, boxShadow: NEU.shadow.insetSm }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: '#212529' }}>Total</span>
            <span style={{ fontSize: 20, fontWeight: 700, color: '#212529' }}>{currency(subtotal)}</span>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 4 }}>Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: 'none', fontSize: 13, resize: 'vertical', boxShadow: NEU.shadow.insetSm, background: '#F8F9FA' }} />
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: '#F8F9FA', cursor: 'pointer', fontSize: 13, boxShadow: NEU.shadow.xs }}>Cancel</button>
            <button type="submit" disabled={saving} style={{
              padding: '10px 20px', borderRadius: 8, border: 'none', background: '#2E8B98', color: '#fff',
              cursor: saving ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, boxShadow: NEU.shadow.sm,
            }}>{saving ? 'Creating...' : 'Create Invoice'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Record Payment Modal ──────────────────────────────────────────────────

function RecordPaymentModal({ invoice, onClose, onRecorded }) {
  const [amount, setAmount] = useState(invoice?.balance || 0);
  const [method, setMethod] = useState('credit_card');
  const [ref, setRef] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (amount <= 0) return;
    setSaving(true);
    try {
      const result = await window.zignagenixAPI.createBillingPayment({
        invoiceId: invoice.id, amount, paymentMethod: method, referenceNumber: ref, notes,
      });
      if (result.error) throw new Error(result.message);
      onRecorded(result);
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#F8F9FA', borderRadius: 16, width: 440, padding: 32, boxShadow: NEU.shadow.lg }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Record Payment</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ADB5BD' }}><X size={18} /></button>
        </div>
        <div style={{ background: '#F8F9FA', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 13, boxShadow: NEU.shadow.insetSm }}>
          <div style={{ fontWeight: 600 }}>{invoice.invoice_number}</div>
          <div style={{ color: '#6C757D' }}>Balance: {currency(invoice.balance)}</div>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 4 }}>Amount</label>
            <input type="number" step="0.01" min="0.01" max={invoice.balance} value={amount}
              onChange={e => setAmount(parseFloat(e.target.value) || 0)}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: 'none', fontSize: 14, boxShadow: NEU.shadow.insetSm, background: '#F8F9FA' }} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 4 }}>Method</label>
            <select value={method} onChange={e => setMethod(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: 'none', fontSize: 13, boxShadow: NEU.shadow.insetSm, background: '#F8F9FA' }}>
              {['credit_card', 'debit_card', 'cash', 'check', 'ach', 'insurance', 'money_order', 'other'].map(m => (
                <option key={m} value={m}>{m.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 4 }}>Reference #</label>
            <input value={ref} onChange={e => setRef(e.target.value)} placeholder="Optional"
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: 'none', fontSize: 13, boxShadow: NEU.shadow.insetSm, background: '#F8F9FA' }} />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
            <button type="button" onClick={onClose} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#F8F9FA', cursor: 'pointer', fontSize: 13, boxShadow: NEU.shadow.xs }}>Cancel</button>
            <button type="submit" disabled={saving || amount <= 0} style={{
              padding: '8px 16px', borderRadius: 8, border: 'none', background: '#62BB46', color: '#fff',
              cursor: saving ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, boxShadow: NEU.shadow.sm,
            }}>{saving ? 'Recording...' : `Record ${currency(amount)}`}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Invoice Detail View ───────────────────────────────────────────────────

function InvoiceDetail({ invoiceId, onBack, userPermissions }) {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPayment, setShowPayment] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const inv = await window.zignagenixAPI.getBillingInvoice(invoiceId);
      if (!inv?.error) setInvoice(inv);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [invoiceId]);

  useEffect(() => { load(); }, [load]);

  async function updateStatus(status) {
    await window.zignagenixAPI.updateBillingInvoiceStatus(invoiceId, status);
    load();
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#6C757D' }}>Loading invoice...</div>;
  if (!invoice) return <div style={{ padding: 40, textAlign: 'center', color: '#DC3545' }}>Invoice not found</div>;

  const canEdit = userPermissions.includes('billing:update');
  const canPay = userPermissions.includes('billing:create');

  return (
    <div>
      <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2E8B98', fontSize: 13, display: 'flex', alignItems: 'center', gap: 4, marginBottom: 16 }}>
        <ChevronLeft size={16} /> Back to Invoices
      </button>

      {/* Invoice Header */}
      <div style={{ background: '#F8F9FA', borderRadius: 12, border: 'none', boxShadow: NEU.shadow.sm, padding: 24, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 20 }}>
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 4px', color: '#212529' }}>{invoice.invoice_number}</h2>
            <p style={{ color: '#6C757D', fontSize: 13, margin: 0 }}>
              {invoice.last_name}, {invoice.first_name} — {invoice.mrn}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <StatusBadge status={invoice.status} />
            {canEdit && invoice.status === 'draft' && (
              <button onClick={() => updateStatus('sent')} style={{
                padding: '6px 12px', borderRadius: 6, border: 'none', background: '#2E8B98', color: '#fff',
                cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4,
              }}><Send size={12} /> Send</button>
            )}
            {canPay && invoice.balance > 0 && (
              <button onClick={() => setShowPayment(true)} style={{
                padding: '6px 12px', borderRadius: 6, border: 'none', background: '#62BB46', color: '#fff',
                cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4,
              }}><CreditCard size={12} /> Record Payment</button>
            )}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, padding: '16px 0', borderTop: 'none', boxShadow: 'inset 0 1px 0 rgba(186,199,216,0.45)' }}>
          <div><div style={{ fontSize: 11, color: '#ADB5BD' }}>Invoice Date</div><div style={{ fontSize: 14, fontWeight: 600 }}>{invoice.invoice_date}</div></div>
          <div><div style={{ fontSize: 11, color: '#ADB5BD' }}>Due Date</div><div style={{ fontSize: 14, fontWeight: 600 }}>{invoice.due_date}</div></div>
          <div><div style={{ fontSize: 11, color: '#ADB5BD' }}>Total</div><div style={{ fontSize: 14, fontWeight: 600 }}>{currency(invoice.total)}</div></div>
          <div><div style={{ fontSize: 11, color: '#ADB5BD' }}>Balance</div><div style={{ fontSize: 14, fontWeight: 700, color: invoice.balance > 0 ? '#DC3545' : '#15803d' }}>{currency(invoice.balance)}</div></div>
        </div>
      </div>

      {/* Line Items */}
      <div style={{ background: '#F8F9FA', borderRadius: 12, border: 'none', boxShadow: NEU.shadow.sm, padding: 24, marginBottom: 16 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, margin: '0 0 12px', color: '#212529' }}>Line Items</h3>
        {(invoice.lineItems || []).map((li, i) => (
          <div key={i} className="responsive-scroll" style={{ borderBottom: 'none', boxShadow: 'inset 0 -1px 0 rgba(186,199,216,0.45)', paddingBottom: 16, marginBottom: 16 }}>
            {/* Primary row: CPT, HCPCS, Description, Qty, Unit Price, Total */}
            <div style={{ display: 'grid', gridTemplateColumns: '80px 90px 1fr 60px 90px 100px', gap: 8, marginBottom: 8, fontSize: 13, color: '#212529', minWidth: 520 }}>
              <div>
                <div style={{ fontSize: 10, color: '#ADB5BD', marginBottom: 2 }}>CPT</div>
                <span style={{ color: '#2E8B98', fontWeight: 600 }}>{li.cpt_code || '—'}</span>
              </div>
              <div>
                <div style={{ fontSize: 10, color: '#ADB5BD', marginBottom: 2 }}>HCPCS</div>
                <span>{li.hcpcs_code || '—'}</span>
              </div>
              <div>
                <div style={{ fontSize: 10, color: '#ADB5BD', marginBottom: 2 }}>Description</div>
                <span>{li.description}</span>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 10, color: '#ADB5BD', marginBottom: 2 }}>Qty</div>
                <span>{li.quantity}</span>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 10, color: '#ADB5BD', marginBottom: 2 }}>Unit Price</div>
                <span>{currency(li.unit_price)}</span>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 10, color: '#ADB5BD', marginBottom: 2 }}>Total</div>
                <span style={{ fontWeight: 600 }}>{currency(li.total)}</span>
              </div>
            </div>
            {/* Secondary row: ICD-10, NDC, Modifier, Place of Service, Dx Pointer, Revenue Code */}
            {(li.icd10_code || li.ndc_code || li.modifier || li.place_of_service || li.diagnosis_pointer || li.revenue_code) && (
              <div style={{ display: 'grid', gridTemplateColumns: '80px 90px 100px 140px 100px 90px', gap: 8, fontSize: 12, color: '#6C757D', background: '#f8f9fa', padding: 8, borderRadius: 6, boxShadow: NEU.shadow.insetSm, minWidth: 600 }}>
                <div>
                  <div style={{ fontSize: 9, color: '#ADB5BD', marginBottom: 2, fontWeight: 600 }}>ICD-10</div>
                  <span>{li.icd10_code || '—'}</span>
                </div>
                <div>
                  <div style={{ fontSize: 9, color: '#ADB5BD', marginBottom: 2, fontWeight: 600 }}>NDC</div>
                  <span>{li.ndc_code || '—'}</span>
                </div>
                <div>
                  <div style={{ fontSize: 9, color: '#ADB5BD', marginBottom: 2, fontWeight: 600 }}>Modifier</div>
                  <span>{li.modifier || '—'}</span>
                </div>
                <div>
                  <div style={{ fontSize: 9, color: '#ADB5BD', marginBottom: 2, fontWeight: 600 }}>Place of Service</div>
                  <span>{li.place_of_service || '—'}</span>
                </div>
                <div>
                  <div style={{ fontSize: 9, color: '#ADB5BD', marginBottom: 2, fontWeight: 600 }}>Dx Pointer</div>
                  <span>{li.diagnosis_pointer || '—'}</span>
                </div>
                <div>
                  <div style={{ fontSize: 9, color: '#ADB5BD', marginBottom: 2, fontWeight: 600 }}>Revenue Code</div>
                  <span>{li.revenue_code || '—'}</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Payments */}
      {(invoice.payments || []).length > 0 && (
        <div style={{ background: '#F8F9FA', borderRadius: 12, border: 'none', boxShadow: NEU.shadow.sm, padding: 24, marginBottom: 16 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, margin: '0 0 12px', color: '#212529' }}>Payment History</h3>
          {invoice.payments.map((p, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: 'none', boxShadow: 'inset 0 -1px 0 rgba(186,199,216,0.45)', fontSize: 13 }}>
              <div>
                <span style={{ fontWeight: 600 }}>{currency(p.amount)}</span>
                <span style={{ color: '#6C757D', marginLeft: 8 }}>via {p.payment_method?.replace(/_/g, ' ')}</span>
                {p.reference_number && <span style={{ color: '#ADB5BD', marginLeft: 8 }}>Ref: {p.reference_number}</span>}
              </div>
              <div style={{ color: '#ADB5BD', fontSize: 12 }}>{p.payment_date} by {p.received_by_name}</div>
            </div>
          ))}
        </div>
      )}

      {showPayment && (
        <RecordPaymentModal invoice={invoice} onClose={() => setShowPayment(false)} onRecorded={() => { setShowPayment(false); load(); }} />
      )}
    </div>
  );
}

// ─── Main Billing Page ─────────────────────────────────────────────────────

function BillingPage({ user }) {
  const [view, setView] = useState('list'); // 'list' | 'detail'
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [patients, setPatients] = useState([]);
  const [feeSchedule, setFeeSchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const permissions = user?.permissions || [];

  // #65 Phase C2b-1: the invoice detail is this page's master->detail sub-view.
  // Register ONE Back step so the global Back (button/keyboard/Android hardware)
  // returns to the invoice LIST (this page's list state) before navigating away
  // — same handler the in-view "Back to Invoices" button uses, which stays.
  // Unconditional hook, above the `view === 'detail'` early return (rules-of-
  // hooks). Label is a LITERAL generic string — never an invoice number, amount,
  // patient, or id (no PHI in the nav stack, which persists to sessionStorage).
  useNavStep({
    active: view === 'detail' && !!selectedInvoiceId,
    label: 'Invoice',
    onBack: () => setView('list'),
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [invs, sum, pts, fees] = await Promise.all([
        window.zignagenixAPI.getBillingInvoices({ status: statusFilter || undefined }),
        window.zignagenixAPI.getBillingSummary(),
        permissions.includes('patients:read') ? window.zignagenixAPI.getPatients() : Promise.resolve([]),
        window.zignagenixAPI.getBillingFeeSchedule(),
      ]);
      setInvoices(Array.isArray(invs) ? invs : []);
      if (sum && !sum.error) setSummary(sum);
      setPatients(Array.isArray(pts) ? pts : []);
      setFeeSchedule(Array.isArray(fees) ? fees : []);
    } catch (err) {
      console.error('Failed to load billing data:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, permissions]);

  useEffect(() => { loadData(); }, [loadData]);

  function openInvoice(id) {
    setSelectedInvoiceId(id);
    setView('detail');
  }

  const filtered = invoices.filter(inv => {
    return matchesNameQuery(
      [inv.invoice_number, inv.last_name, inv.first_name, inv.mrn], searchTerm);
  });

  function exportCSV() {
    const headers = ['Invoice #', 'Patient', 'MRN', 'Date', 'Due', 'Status', 'Total', 'Paid', 'Balance'];
    const rows = filtered.map(inv => [
      inv.invoice_number, `${inv.last_name} ${inv.first_name}`, inv.mrn,
      inv.invoice_date, inv.due_date, inv.status,
      inv.total, inv.amount_paid, inv.balance,
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'invoices.csv'; a.click();
    URL.revokeObjectURL(url);
  }

  if (view === 'detail' && selectedInvoiceId) {
    return (
      <div className="page" style={{ padding: 24 }}>
        <InvoiceDetail invoiceId={selectedInvoiceId} onBack={() => setView('list')} userPermissions={permissions} />
      </div>
    );
  }

  return (
    <div className="page" style={{ padding: 24, overflowY: 'auto', background: '#F8F9FA', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: 'linear-gradient(135deg, #62BB46, #4FA038)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Receipt size={22} color="#fff" />
          </div>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0, color: '#212529' }}>Patient Billing</h1>
            <p style={{ fontSize: 12, color: '#6C757D', margin: 0 }}>Invoices, payments, and account balances</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <PrintButton pageTitle="Patient Billing" subtitle="Invoices, Payments & Account Balances" />
          <button onClick={exportCSV} style={{
            padding: '8px 14px',
            cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4,
          }}><Download size={14} /> Export</button>
          {permissions.includes('billing:create') && (
            <button onClick={() => setShowCreate(true)} style={{
              padding: '8px 14px', borderRadius: 8, border: 'none', background: '#2E8B98', color: '#fff',
              cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4,
            }}><Plus size={14} /> New Invoice</button>
          )}
        </div>
      </div>

      {/* KPIs */}
      {summary && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
          <KPI icon={FileText} label="Total Invoices" value={summary.totalInvoices} color="#2E8B98" />
          <KPI icon={DollarSign} label="Total Billed" value={currency(summary.totalBilled)} color="#256F7A" />
          <KPI icon={CheckCircle} label="Collected" value={currency(summary.totalCollected)} color="#62BB46" />
          <KPI icon={AlertTriangle} label="Outstanding" value={currency(summary.totalOutstanding)} color="#DC3545"
            sub={`${summary.overdueCount} overdue`} />
          <KPI icon={Clock} label="Pending" value={summary.draftCount + summary.sentCount + summary.partialCount} color="#F26522"
            sub={`${summary.draftCount} draft, ${summary.sentCount} sent`} />
        </div>
      )}

      {/* AI Billing Report — Phase 2 of the Admin AI distribution.
          One-shot report generator embedded inline so staff don't have to
          context-switch to the centralized Admin AI surface for billing
          insights. Permission gated on billing:read inside the backend. */}
      <AIReportPanel
        resource="billing"
        title="AI Billing Report"
        description="A/R health check + top accounts to call today. Generated on demand from current invoice & payment data."
      />

      {/* A/R Aging */}
      {summary?.aging?.length > 0 && (
        <div style={{ background: '#F8F9FA', borderRadius: 12, border: 'none', boxShadow: NEU.shadow.sm, padding: '16px 20px', marginBottom: 20 }}>
          <h3 style={{ fontSize: 13, fontWeight: 600, margin: '0 0 12px', color: '#212529' }}>Accounts Receivable Aging</h3>
          <div style={{ display: 'flex', gap: 12 }}>
            {summary.aging.map((a, i) => (
              <div key={i} style={{ flex: 1, background: '#F8F9FA', borderRadius: 8, padding: '10px 12px', textAlign: 'center', boxShadow: NEU.shadow.insetSm }}>
                <div style={{ fontSize: 11, color: '#6C757D', marginBottom: 4 }}>{a.bucket}</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#212529' }}>{currency(a.amount)}</div>
                <div style={{ fontSize: 10, color: '#ADB5BD' }}>{a.count} invoices</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 10, color: '#ADB5BD' }} />
          <input placeholder="Search invoices, patients, MRNs..." value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{ width: '100%', padding: '8px 8px 8px 30px', borderRadius: 8, border: 'none', fontSize: 13, boxShadow: NEU.shadow.insetSm, background: '#F8F9FA' }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Filter size={14} color="#6C757D" />
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            style={{ padding: '8px 10px', borderRadius: 8, border: 'none', fontSize: 12, boxShadow: NEU.shadow.insetSm, background: '#F8F9FA' }}>
            <option value="">All Statuses</option>
            {Object.entries(STATUS_COLORS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        <button onClick={loadData} style={{
          padding: '8px 12px', borderRadius: 8, border: 'none', background: '#F8F9FA',
          cursor: 'pointer', display: 'flex', alignItems: 'center', boxShadow: NEU.shadow.xs,
        }}><RefreshCw size={14} color="#6C757D" /></button>
      </div>

      {/* Invoice Table — wrapped in responsive-scroll so wide rows scroll
          horizontally on phones instead of overflowing the whole page. */}
      <div className="responsive-scroll" style={{ background: '#F8F9FA', borderRadius: 12, border: 'none', boxShadow: NEU.shadow.sm, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr 100px 80px 100px 100px 100px 60px', gap: 8, padding: '10px 16px', background: '#F8F9FA', borderBottom: 'none', boxShadow: 'inset 0 -1px 0 rgba(186,199,216,0.45)', fontSize: 11, fontWeight: 600, color: '#6C757D', minWidth: 720 }}>
          <span>Invoice #</span><span>Patient</span><span>Date</span><span>Status</span><span style={{ textAlign: 'right' }}>Total</span><span style={{ textAlign: 'right' }}>Paid</span><span style={{ textAlign: 'right' }}>Balance</span><span></span>
        </div>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#6C757D' }}><RefreshCw size={20} style={{ animation: 'spin 1s linear infinite' }} /></div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#ADB5BD', fontSize: 13 }}>No invoices found</div>
        ) : (
          filtered.map((inv, i) => (
            <div key={inv.id} style={{
              display: 'grid', gridTemplateColumns: '140px 1fr 100px 80px 100px 100px 100px 60px', gap: 8,
              padding: '12px 16px', borderBottom: 'none', boxShadow: 'inset 0 -1px 0 rgba(186,199,216,0.45)', fontSize: 13, color: '#212529',
              background: i % 2 === 1 ? '#fafbfc' : '#fff', cursor: 'pointer',
              transition: 'background 0.1s', minWidth: 720,
            }}
            onClick={() => openInvoice(inv.id)}
            onMouseEnter={e => e.currentTarget.style.background = '#f0f7ff'}
            onMouseLeave={e => e.currentTarget.style.background = i % 2 === 1 ? '#fafbfc' : '#fff'}
            >
              <span style={{ fontWeight: 600, color: '#2E8B98' }}>{inv.invoice_number}</span>
              <span>{inv.last_name}, {inv.first_name} <span style={{ color: '#ADB5BD', fontSize: 11 }}>({inv.mrn})</span></span>
              <span style={{ fontSize: 12 }}>{inv.invoice_date}</span>
              <span><StatusBadge status={inv.status} /></span>
              <span style={{ textAlign: 'right' }}>{currency(inv.total)}</span>
              <span style={{ textAlign: 'right', color: '#62BB46' }}>{currency(inv.amount_paid)}</span>
              <span style={{ textAlign: 'right', fontWeight: 600, color: parseFloat(inv.balance) > 0 ? '#DC3545' : '#15803d' }}>{currency(inv.balance)}</span>
              <span style={{ textAlign: 'center' }}><Eye size={14} color="#ADB5BD" /></span>
            </div>
          ))
        )}
      </div>

      {showCreate && (
        <CreateInvoiceModal patients={patients} feeSchedule={feeSchedule}
          onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); loadData(); }} />
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default BillingPage;
